import { useEffect, useState } from 'react'
import { apiService, ApiError } from '../services/api'
import type { TenantRole, TenantUser } from '../types'
import TabNavigation from '../components/TabNavigation'
import { useAuth } from '../context/AuthContext'
import { useLanguage } from '../context/LanguageContext'
import { useNotification } from '../context/NotificationContext'

/**
 * Admin-only screen to manage who has access to the tenant.
 * The backend is the authority (every /api/users call 403s for a plain user);
 * the role check here only keeps the UI honest.
 */
export default function UserManagement() {
  const { t, language } = useLanguage()
  const { role, email: currentEmail } = useAuth()
  const { showNotification } = useNotification()

  const [users, setUsers] = useState<TenantUser[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [newEmail, setNewEmail] = useState('')
  const [newRole, setNewRole] = useState<TenantRole>('user')
  const [adding, setAdding] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  /** Email of the row currently mutating, so we can disable just those buttons. */
  const [busyEmail, setBusyEmail] = useState<string | null>(null)

  const isAdmin = role === 'admin'

  /** Map an API error code (or status) onto a localized message. */
  const messageFor = (error: unknown): string => {
    if (error instanceof ApiError) {
      switch (error.code) {
        case 'wrong_domain':
          return t.users.errWrongDomain
        case 'already_exists':
          return t.users.errAlreadyExists
        case 'last_admin':
          return t.users.errLastAdmin
        case 'invalid_email':
          return t.users.errInvalidEmail
      }
      if (error.statusCode === 403) return t.users.errForbidden
    }
    return t.users.errGeneric
  }

  const loadUsers = async () => {
    setLoading(true)
    try {
      const data = await apiService.listUsers()
      setUsers(data)
      setLoadError(null)
    } catch (error) {
      console.error('Error loading users:', error)
      setLoadError(messageFor(error))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isAdmin) loadUsers()
    else setLoading(false)
  }, [isAdmin])

  const isSelf = (email: string) =>
    !!currentEmail && email.toLowerCase() === currentEmail.toLowerCase()

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    const email = newEmail.trim().toLowerCase()
    if (!email) return
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError(t.users.errInvalidEmail)
      return
    }

    setAdding(true)
    setFormError(null)
    try {
      const created = await apiService.addUser(email, newRole)
      setUsers((prev) => [...prev, created])
      setNewEmail('')
      setNewRole('user')
      showNotification('success', t.users.added, t.users.addedDetail.replace('{email}', email))
    } catch (error) {
      console.error('Error adding user:', error)
      setFormError(messageFor(error))
    } finally {
      setAdding(false)
    }
  }

  const applyPatch = async (user: TenantUser, patch: { role?: TenantRole; disabled?: boolean }) => {
    setBusyEmail(user.email)
    try {
      const updated = await apiService.updateUser(user.email, patch)
      setUsers((prev) => prev.map((u) => (u.email === user.email ? updated : u)))
      showNotification('success', t.users.updated, updated.email)
    } catch (error) {
      console.error('Error updating user:', error)
      showNotification('error', t.common.error, messageFor(error))
    } finally {
      setBusyEmail(null)
    }
  }

  const handleToggleRole = (user: TenantUser) => {
    const nextRole: TenantRole = user.role === 'admin' ? 'user' : 'admin'
    // Demoting yourself costs you this screen: make that explicit.
    if (nextRole === 'user' && isSelf(user.email)) {
      if (!window.confirm(t.users.confirmDemoteSelf.replace('{email}', user.email))) return
    }
    applyPatch(user, { role: nextRole })
  }

  const handleToggleDisabled = (user: TenantUser) => {
    const nextDisabled = !user.disabled
    if (nextDisabled && isSelf(user.email)) {
      if (!window.confirm(t.users.confirmDisableSelf.replace('{email}', user.email))) return
    }
    applyPatch(user, { disabled: nextDisabled })
  }

  const handleDelete = async (user: TenantUser) => {
    // Deleting your own account locks you out; the copy says so.
    const prompt = isSelf(user.email) ? t.users.confirmRemoveSelf : t.users.confirmRemove
    if (!window.confirm(prompt.replace('{email}', user.email))) return

    setBusyEmail(user.email)
    try {
      await apiService.deleteUser(user.email)
      setUsers((prev) => prev.filter((u) => u.email !== user.email))
      showNotification(
        'success',
        t.users.removed,
        t.users.removedDetail.replace('{email}', user.email)
      )
    } catch (error) {
      console.error('Error removing user:', error)
      showNotification('error', t.common.error, messageFor(error))
    } finally {
      setBusyEmail(null)
    }
  }

  const formatDate = (value: string | null) => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'
    return date.toLocaleDateString(language === 'nl' ? 'nl-NL' : 'en-US')
  }

  // Never render management controls for a non-admin, even if they guess the URL.
  if (!isAdmin) {
    return (
      <>
        <TabNavigation />
        <div className="p-6">
          <div className="max-w-7xl mx-auto">
            <div className="glass-card rounded-2xl p-6">
              <p className="text-gray-700">{t.users.adminOnly}</p>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <TabNavigation />
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="glass-card rounded-2xl p-6 mb-6">
            <h1 className="text-3xl font-bold text-gray-800">{t.users.title}</h1>
            <p className="text-gray-600 mt-1">{t.users.subtitle}</p>
          </div>

          {/* Add user */}
          <div className="glass-card rounded-2xl p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">{t.users.addTitle}</h2>
            <form onSubmit={handleAdd} className="flex flex-col md:flex-row md:items-end gap-4">
              <div className="flex-1">
                <label
                  htmlFor="new-user-email"
                  className="block text-sm font-semibold text-gray-600 mb-1"
                >
                  {t.users.emailLabel}
                </label>
                <input
                  id="new-user-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value)
                    setFormError(null)
                  }}
                  placeholder={t.users.emailPlaceholder}
                  className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#FBB300]"
                />
              </div>
              <div className="md:w-56">
                <label
                  htmlFor="new-user-role"
                  className="block text-sm font-semibold text-gray-600 mb-1"
                >
                  {t.users.roleLabel}
                </label>
                <select
                  id="new-user-role"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value as TenantRole)}
                  className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#FBB300]"
                >
                  <option value="user">{t.users.roleUser}</option>
                  <option value="admin">{t.users.roleAdmin}</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={adding || newEmail.trim() === ''}
                className="px-6 py-2 rounded-lg text-sm font-semibold text-[#1A1A1A] bg-[#FBB300] hover:bg-[#e6a300] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {adding ? t.users.adding : t.users.add}
              </button>
            </form>
            {formError && <p className="text-sm text-red-600 mt-3">{formError}</p>}
          </div>

          {/* Users table */}
          <div className="glass-card rounded-2xl p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {t.users.title}
              {!loading && !loadError && (
                <span className="text-gray-500 font-normal ml-2">({users.length})</span>
              )}
            </h2>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="loading-spinner"></div>
              </div>
            ) : loadError ? (
              <p className="text-sm text-red-600 py-6">{loadError}</p>
            ) : users.length === 0 ? (
              <p className="text-sm text-gray-500 py-6">{t.users.empty}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        {t.users.colEmail}
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        {t.users.colRole}
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        {t.users.colStatus}
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        {t.users.colAddedBy}
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        {t.users.colAddedOn}
                      </th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">
                        {t.users.colActions}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => {
                      const busy = busyEmail === user.email
                      const self = isSelf(user.email)
                      return (
                        <tr
                          key={user.email}
                          className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center text-gray-900 font-bold text-sm">
                                {user.email.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm font-medium text-gray-800">{user.email}</span>
                              {self && (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full border bg-gray-100 text-gray-700 border-gray-300">
                                  {t.users.you}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`px-2 py-1 text-xs font-semibold rounded-full border ${
                                user.role === 'admin'
                                  ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                                  : 'bg-blue-100 text-blue-800 border-blue-300'
                              }`}
                            >
                              {user.role === 'admin' ? t.users.roleAdmin : t.users.roleUser}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`px-2 py-1 text-xs font-semibold rounded-full border ${
                                user.disabled
                                  ? 'bg-gray-100 text-gray-800 border-gray-300'
                                  : 'bg-green-100 text-green-800 border-green-300'
                              }`}
                            >
                              {user.disabled ? t.users.statusDisabled : t.users.statusActive}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {user.added_by || t.users.unknown}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {formatDate(user.created_at)}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => handleToggleRole(user)}
                                disabled={busy}
                                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {user.role === 'admin' ? t.users.makeUser : t.users.makeAdmin}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleToggleDisabled(user)}
                                disabled={busy}
                                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {user.disabled ? t.users.enable : t.users.disable}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(user)}
                                disabled={busy}
                                className="px-3 py-1 text-sm bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {t.users.remove}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
